import { useState, useEffect, useCallback } from 'react';
import { PaymentMethod } from '../api/models/PaymentMethod';
import { PAYMENT_METHODS } from '../utils/constants/paymentMethods';
import { SalesCatalogServiceHelpers } from '../utils/api-helpers/SalesCatalogService.helpers';

function normalizeActivePaymentMethodsResponse(response: unknown): PaymentMethod[] {
  if (Array.isArray(response)) {
    return response;
  }
  if (response != null && typeof response === 'object' && 'data' in response) {
    const data = (response as { data?: unknown }).data;
    return Array.isArray(data) ? (data as PaymentMethod[]) : [];
  }
  return [];
}

function filterNonStandardActiveMethods(methods: PaymentMethod[]): PaymentMethod[] {
  return methods.filter((method) => {
    const isActive = method.isActive ?? true;
    const isNotStandard =
      method.code !== PAYMENT_METHODS.CASH &&
      method.code !== PAYMENT_METHODS.CARD &&
      method.code !== PAYMENT_METHODS.QR;
    return isActive && isNotStandard;
  });
}

export interface UseOtherPaymentMethodsOptions {
  enabled?: boolean;
}

/**
 * Loads non-standard payment methods for sales flows (nueva venta, procesar pago, intercambio).
 * Uses /sales-catalog/payment-methods so seller roles can read them without settings.properties.
 */
export function useOtherPaymentMethods(options: UseOtherPaymentMethodsOptions = {}) {
  const { enabled = true } = options;
  const [otherPaymentMethods, setOtherPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const response = await SalesCatalogServiceHelpers.getPaymentMethods();
        if (cancelled) {
          return;
        }
        const allMethods = normalizeActivePaymentMethodsResponse(response);
        setOtherPaymentMethods(filterNonStandardActiveMethods(allMethods));
      } catch (error) {
        console.error('Error fetching payment methods:', error);
        if (!cancelled) {
          setOtherPaymentMethods([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const getPaymentMethodName = useCallback(
    (methodCode: string): string => {
      if (methodCode === PAYMENT_METHODS.CASH) return 'Efectivo';
      if (methodCode === PAYMENT_METHODS.CARD) return 'Tarjeta';
      if (methodCode === PAYMENT_METHODS.QR) return 'QR';
      const otherMethod = otherPaymentMethods.find((m) => m.code === methodCode);
      return otherMethod?.name ?? methodCode;
    },
    [otherPaymentMethods],
  );

  return { otherPaymentMethods, isLoading, getPaymentMethodName };
}
